import { Component, Input, Output, EventEmitter, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

type ToggleTag = string | { label: string; color: string };

@Component({
  selector: 'app-toggle-option',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatSlideToggleModule, FormsModule],
  templateUrl: './toggle-option.component.html',
  styleUrls: ['./toggle-option.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ToggleOptionComponent),
      multi: true
    }
  ]
})
export class ToggleOptionComponent implements ControlValueAccessor {
  @Input() title: string = '';
  @Input() description: string = '';
  @Input() icon: string = 'settings';
  @Input() iconColor: string = 'blue';
  @Input() showExpandedContent: boolean = false;
  @Input() containerClass: string = '';
  @Input() tags: ToggleTag[] = [];
  @Output() valueChange = new EventEmitter<boolean>();

  value: boolean = false;
  disabled: boolean = false;

  private onChange: (value: boolean) => void = () => {};
  private onTouched: () => void = () => {};

  get iconColorClass(): string {
    const colorMap: { [key: string]: string } = {
      'blue': 'text-blue-600',
      'green': 'text-green-600',
      'purple': 'text-purple-600',
      'orange': 'text-orange-600',
      'red': 'text-red-600',
      'indigo': 'text-indigo-600',
      'yellow': 'text-yellow-600',
      'gray': 'text-gray-600'
    };
    return colorMap[this.iconColor] || 'text-gray-600';
  }

  getTagLabel(tag: ToggleTag): string {
    return typeof tag === 'string' ? tag : tag.label;
  }

  getTagClass(tag: ToggleTag): string {
    if (typeof tag === 'string') {
      return 'border border-gray-200';
    } else {
      const colorMap: { [key: string]: string } = {
        green: 'border border-green-600',
        red: 'border border-red-600',
        orange: 'border border-orange-600',
        purple: 'border border-purple-600',
        blue: 'border border-blue-600',
        yellow: 'border border-yellow-500',
        gray: 'border border-gray-200'
      };
      return colorMap[tag.color] || 'border border-gray-200';
    }
  }

  writeValue(value: boolean): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onToggleChange(event: any): void {
    this.value = event.checked;
    this.onChange(this.value);
    this.onTouched();
    this.valueChange.emit(this.value);
  }
}